import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled, { css } from 'styled-components';
import { Link } from '@helpcenter/analytics-components/src/link';
import type { SearchProps } from '@helpcenter/result-base';
import * as fonts from '@helpcenter/styles/src/fonts';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import * as transitions from '@helpcenter/styles/src/transitions';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import IconTile from '@atlaskit/icon/icon-tile';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import DocumentIcon from '@atlaskit/icon/core/page';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/box';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { addSourceToUrl } from '@atlassian/help-center-common-util/article-util';
import {
	isThirdPartyArticle,
	urlRefsAndTarget,
} from '@atlassian/help-center-common-util/is-external-resource';
import GoogleDriveIcon from '@atlassian/third-party-connectors/src/common/ui/icons/google-drive-icon';
import SharepointIcon from '@atlassian/third-party-connectors/src/common/ui/icons/sharepoint-icon';
import type { SourceType } from 'rest/search/kbs/types';
interface KbSearchProps extends SearchProps {
	titleHtml: string;
	to: string;
	actionSubjectId: string;
	snippet?: string;
	openNewTab?: boolean;
	isSuggestion?: boolean;
	showPortalName?: boolean;
	portalName?: string;
	sourceType?: SourceType;
}

const KbSnippet: React.FC<KbSearchProps> = ({
	titleHtml,
	to,
	actionSubjectId,
	openNewTab,
	onClick,
	snippet,
	portalName,
	sourceType,
	showPortalName = false,
	...props
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isAnExternalResource = isThirdPartyArticle(sourceType);
	const highlightHtmlSanitizeConfig = {
		FORBID_TAGS: ['style'],
		FORBID_ATTR: ['style'],
	};
	const sanitizeHighlightHtml = (html: string) => {
		return isomorphicSanitize(html, highlightHtmlSanitizeConfig);
	};
	const linkProps = urlRefsAndTarget({
		isAnExternalResource,
		displayLink: addSourceToUrl(to, 'request-form', isAnExternalResource),
	});

	const getIcon = () => {
		const IconInTile = ({ label }: NewCoreIconProps) => {
			if (sourceType === 'GOOGLE_DRIVE') {
				return <GoogleDriveIcon label={label} size="medium" />;
			}
			if (sourceType === 'SHAREPOINT') {
				return <SharepointIcon label={label} size="medium" />;
			}
			return <DocumentIcon label={label} />;
		};

		return <IconTile appearance="blue" icon={IconInTile} label="Article icon" size="medium" />;
	};

	React.useEffect(() => {
		createAnalyticsEvent({
			analyticsType: UI_EVENT_TYPE,
			action: 'viewed',
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions, styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions -- Disabling existing violation, should be fixed when revisited.
		<LinkContainer
			key={to}
			onClick={onClick}
			actionSubjectId={actionSubjectId}
			data-test-id="article-link-container"
			{...props}
			{...linkProps}
		>
			<Box>{getIcon()}</Box>
			<ArticleContainer snippets={snippet}>
				<Title
					dangerouslySetInnerHTML={{
						__html: sanitizeHighlightHtml(titleHtml),
					}}
				/>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				{showPortalName && portalName && <ServiceDeskTitle>from {portalName}</ServiceDeskTitle>}
				{snippet && (
					<Snippet
						dangerouslySetInnerHTML={{
							__html: sanitizeHighlightHtml(snippet),
						}}
					/>
				)}
			</ArticleContainer>
		</LinkContainer>
	);
};
export default withAnalyticsContext({ componentName: 'KbSnippet' })(KbSnippet);

interface LinkContainerProps {
	isSuggestion?: boolean;
}

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TitleH3Css = css`
	${fonts.regular};
	overflow: hidden;
	text-overflow: ellipsis;
	/* stylelint-disable-next-line value-no-vendor-prefix */
	display: -webkit-box;
	-webkit-line-clamp: 1;
	word-break: break-word;
	/* stylelint-disable-next-line property-no-vendor-prefix */
	-webkit-box-orient: vertical;
	text-transform: uppercase;
	margin: ${token('space.0', '0px')};
	font-weight: normal;
	transition: color ${transitions.speedMs.medium}ms ${transitions.timingFunctions.easeInOut};
	color: ${token('color.text', '#172B4D')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Title = styled.h3`
	${TitleH3Css}
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const SnippetParagraphCss = css`
	${fonts.small};
	color: ${token('color.text', '#172B4D')};
	margin-top: ${token('space.100', '8px')};
	padding-right: ${token('space.200', '16px')};
	overflow: hidden;
	text-overflow: ellipsis;
	/* stylelint-disable-next-line value-no-vendor-prefix */
	display: -webkit-box;
	-webkit-line-clamp: 3;
	/* stylelint-disable-next-line property-no-vendor-prefix */
	-webkit-box-orient: vertical;
	line-height: ${token('space.250', '20px')};
	word-break: break-word;
	white-space: normal;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Snippet = styled.p`
	${SnippetParagraphCss}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LinkContainer = styled(Link)<LinkContainerProps>`
	${mixins.borderRadius};
	${(props) => mixins.applyIf(!props.isSuggestion, mixins.raiseUpOnHoverOrFocus())};
	display: flex;
	/* stylelint-disable-next-line scale-unlimited/declaration-strict-value */
	background-color: ${(props) =>
		props.isSuggestion ? 'none' : token('elevation.surface.raised', '#FFFFFF')};
	padding: ${token('space.200', '16px')} ${token('space.050', '4px')} ${token('space.200', '16px')}
		${token('space.150', '12px')};
	/* stylelint-disable-next-line scale-unlimited/declaration-strict-value */
	margin: ${(props) =>
		props.isSuggestion ? '0' : `${token('space.100', '8px')} 0 ${token('space.200', '16px')}`};
	&:hover,
	&:focus {
		/* stylelint-disable-next-line scale-unlimited/declaration-strict-value */
		background: ${(props) =>
			props.isSuggestion
				? token('color.background.neutral.subtle.hovered', '#EBECF0')
				: token('elevation.surface.raised', '#FFFFFF')};
		text-decoration: none;
	}
	&:hover h3,
	&:focus h3 {
		color: ${(props) => token('color.link', props.theme.color)};
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ArticleContainer = styled.div<{ snippets?: string }>`
	margin-left: ${token('space.250', '20px')};
	${(props) =>
		!props.snippets
			? `
            display: flex;
            align-items: center
            `
			: ''}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ServiceDeskTitle = styled.div`
	color: ${token('color.text.subtlest', '#7A869A')};
	font-size: ${grid.multiple(1.375).px};
`;
